import { Form } from "antd"
import { keyBy } from "lodash"
import { useMemo } from "react"

import {
  classAtoms,
  classSessionAtoms,
  makeupAtoms,
  projectAtoms,
  tryoutAtoms,
} from "#atoms"
import { NULL_ID } from "#classes/Firebase"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { type ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import {
  type AddMakeupFormValues,
  type MakeupConstructor,
} from "#customTypes/composedTypes/makeupTypes"
import { type ProjectConstructor } from "#customTypes/composedTypes/projectTypes"
import { type TryoutConstructor } from "#customTypes/composedTypes/tryoutTypes"
import {
  useIsAllFulfilled,
  useIsFulfilled,
} from "#hooks/jotai/useCascadingData"
import { useGetOneStudent } from "#hooks/jotai/useGetStudents"
import {
  useFilteredDocs,
  useOneDoc,
  useSomeDocs,
  useStableIds,
} from "#hooks/jotai/useSubscription"
import { getNow } from "#utils/dateHelpers"
import { addOneSemester } from "#utils/semesterHelpers"

import {
  coalesceMakeupFilterDate,
  getMakeupSessionWindow,
  selectConflictSemesters,
  selectStudentClassSessionIds,
} from "./makeupSessionWindow"
import {
  useMakeupMissedSessionOptions,
  useMakeupSessionOptions,
} from "./useMakeupFormOptions"

const NULL_QUERY_CONFIG: FirebaseQueryConfig = {
  queries: {
    id: {
      value: NULL_ID,
      comparator: COMPARATORS.EQUALS,
    },
  },
}

const useMakeupFormData = (initialValues: AddMakeupFormValues | undefined) => {
  const form = Form.useFormInstance()
  // TODO: Add semester filter
  // const chosenSemester = getCurrentSemester()

  // Get form values
  const studentId =
    Form.useWatch("studentId", form) ?? initialValues?.studentId ?? null
  const missedClassId =
    Form.useWatch("missedClassId", form) ?? initialValues?.missedClassId ?? null
  const missedSessionId =
    Form.useWatch("missedSessionId", form) ??
    initialValues?.missedSessionId ??
    null
  const makeupClassId =
    Form.useWatch("makeupClassId", form) ?? initialValues?.makeupClassId ?? null
  const makeupSessionId =
    Form.useWatch("makeupSessionId", form) ??
    initialValues?.makeupSessionId ??
    null
  const makeupFilterDate = coalesceMakeupFilterDate(
    Form.useWatch("makeupFilterDate", form),
    initialValues?.makeupFilterDate
  )
  const isScheduledForNextSemester: boolean =
    Form.useWatch("isScheduledForNextSemester", form) ??
    initialValues?.isScheduledForNextSemester

  // Get student
  const student = useGetOneStudent(studentId)

  const matchStudentIdQueryConfig = useMemo(() => {
    return {
      queries: {
        studentId: { value: studentId, comparator: COMPARATORS.EQUALS },
      },
    }
  }, [studentId])

  // Get all makeups for this student this semester
  const studentMakeups = useFilteredDocs<MakeupConstructor>(
    makeupAtoms,
    DOC_TYPES.MAKEUPS,
    matchStudentIdQueryConfig
  )

  // Get all tryouts for this student this semester
  const studentTryouts = useFilteredDocs<TryoutConstructor>(
    tryoutAtoms,
    DOC_TYPES.TRYOUTS,
    matchStudentIdQueryConfig
  )

  const studentClassIds = useMemo(
    () => Object.keys(student?.attendance ?? {}),
    [student]
  )

  // Get student classes
  const studentClasses = useSomeDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    studentClassIds
  )

  const classesFulfilled = useIsFulfilled(studentClassIds, studentClasses, 3000)

  // Candidate window for the makeup picker (null until a filter date exists).
  const sessionWindow = useMemo(
    () => getMakeupSessionWindow(makeupFilterDate, getNow()),
    [makeupFilterDate]
  )

  // Get missed class
  const missedClass = useMemo(() => {
    if (!missedClassId || !studentClasses?.length) return null

    const classObj = studentClasses.find(
      (item) => item?.id && item.id === missedClassId
    )

    return classObj
  }, [missedClassId, studentClasses])

  // Get classes with same curriculum
  const classQueryConfig = useMemo(() => {
    if (!missedClassId || !missedClass?.curriculumId) return NULL_QUERY_CONFIG

    const nextSemester = addOneSemester(missedClass?.semester)
    return {
      queries: isScheduledForNextSemester
        ? {
            semester: {
              value: nextSemester,
              comparator: COMPARATORS.EQUALS,
            },
          }
        : {
            curriculumId: {
              value: missedClass?.curriculumId ?? null,
              comparator: COMPARATORS.EQUALS,
            },
          },
    }
  }, [
    missedClass?.curriculumId,
    isScheduledForNextSemester,
    missedClassId,
    missedClass?.semester,
  ])

  const possibleMakeupClassesFromDb = useFilteredDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    classQueryConfig
  )

  // Get possible makeup classes
  const possibleMakeupClasses = useMemo(
    () =>
      possibleMakeupClassesFromDb.filter(
        (item) => item?.id && item.id !== missedClass?.id
      ),
    [possibleMakeupClassesFromDb, missedClass]
  )

  const possibleMakeupClassesById = useMemo(
    () => keyBy(possibleMakeupClasses, "id"),
    [possibleMakeupClasses]
  )

  // Conflict-source scope: the missed class plus the student's classes in every
  // semester a candidate can come from, derived from the candidate classes
  // loaded above (plus missed + next as a pre-warm). Intentionally independent
  // of makeupFilterDate — see selectStudentClassSessionIds for why tying it to
  // the picker window broke date editing. Typing in the date filter changes
  // sessionQueryConfig only; none of these inputs move, so the memo below and
  // the subscription it feeds stay put.
  const conflictSemesters = useMemo(
    () =>
      selectConflictSemesters({
        missedClass,
        candidateClasses: possibleMakeupClasses,
      }),
    [missedClass, possibleMakeupClasses]
  )

  const studentClassSessionIds = useMemo(
    () =>
      selectStudentClassSessionIds({
        studentClasses,
        missedClassId,
        semesters: conflictSemesters,
      }),
    [studentClasses, missedClassId, conflictSemesters]
  )
  const stableStudentClassSessionIds = useStableIds(studentClassSessionIds)

  // Get all sessions for all student classes
  const studentClassSessions = useSomeDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    stableStudentClassSessionIds
  )

  const studentClassSessionsFulfilled = useIsFulfilled(
    stableStudentClassSessionIds,
    studentClassSessions,
    3000
  )

  // Get missed class sessions
  const missedClassSessions = useMemo(() => {
    if (!missedClassId || !studentClassSessions?.length) return null

    return studentClassSessions
      .filter((session) => session.classId === missedClassId)
      .sort((a, b) => a.eventStart.localeCompare(b.eventStart))
  }, [missedClassId, studentClassSessions])

  // Get missed session
  const missedSession = useMemo(() => {
    if (!missedSessionId || !missedClassSessions) return null

    return missedClassSessions?.find(
      (item) => item?.id && item.id === missedSessionId
    )
  }, [missedClassSessions, missedSessionId])

  // Get missed project
  const missedProject = useOneDoc<ProjectConstructor>(
    projectAtoms,
    DOC_TYPES.PROJECTS,
    missedSession?.projectId
  )

  const missedProjectFulfilled = missedSession?.projectId
    ? !!missedProject
    : true

  // Get possible makeup class sessions
  const sessionQueryConfig = useMemo(() => {
    if (!sessionWindow) return NULL_QUERY_CONFIG

    return {
      queries: {
        eventStart: {
          value: [
            sessionWindow.start.toISOString(),
            sessionWindow.end.toISOString(),
          ],
          comparator: COMPARATORS.BETWEEN,
        },
      },
    }
  }, [sessionWindow])

  const makeupClassSessions = useFilteredDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    sessionQueryConfig
  )

  const initialMakeupSession = useOneDoc<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    initialValues?.makeupSessionId ?? undefined
  )

  const possibleMakeupClassSessions = useMemo(() => {
    if (!possibleMakeupClassesById) return []

    const fromQuery = makeupClassSessions.filter(
      (item) => possibleMakeupClassesById[item.classId]
    )
    if (
      initialMakeupSession &&
      possibleMakeupClassesById[initialMakeupSession.classId] &&
      !fromQuery.some((s) => s.id === initialMakeupSession.id)
    ) {
      return [...fromQuery, initialMakeupSession]
    }
    return fromQuery
  }, [makeupClassSessions, possibleMakeupClassesById, initialMakeupSession])

  // Get makeup class
  const makeupClass = possibleMakeupClasses?.find(
    (item) => item?.id && item.id === makeupClassId
  )

  // Get makeup session
  const makeupSession = useMemo(() => {
    if (!makeupSessionId || !makeupClassSessions) return null

    return makeupClassSessions?.find(
      (item) => item?.id && item.id === makeupSessionId
    )
  }, [makeupClassSessions, makeupSessionId])

  const missedSessionOptions = useMakeupMissedSessionOptions({
    missedClassId,
    missedClassSessions,
    studentAttendance: student?.attendance,
    studentMakeups,
    studentTryouts,
    initialValues,
    isSummerCamp: !!missedClass?.isSummerCamp,
  })

  const makeupSessionOptions = useMakeupSessionOptions({
    missedClassId,
    studentClassSessions,
    studentMakeups,
    studentTryouts,
    possibleMakeupClasses,
    possibleMakeupClassSessions,
    studentAttendance: student?.attendance,
    initialValues,
    makeupFilterDate,
  })

  const isReady = useIsAllFulfilled([
    classesFulfilled,
    studentClassSessionsFulfilled,
    missedProjectFulfilled,
  ])

  return {
    student,
    studentMakeups,
    studentTryouts,
    studentClasses,
    studentClassSessions,
    missedClass,
    missedClassSessions,
    missedSession,
    missedProject,
    possibleMakeupClasses,
    possibleMakeupClassSessions,
    makeupClass,
    makeupSession,
    initialMakeupSession,
    missedSessionOptions,
    makeupSessionOptions,
    isReady,
    errors: [], // Errors are handled at the subscription level
  }
}

export default useMakeupFormData
